// The filing panels on Claims → Runs: where each file stands with GAMMIS,
// the state of the SFTP transfer, and what pre-flight said.
//
// Declared inside the page component until 2026-10-20, which made React treat
// each one as a new component on every render and remount it.

import { format } from 'date-fns';
import { nextStep } from '../../../lib/edi/submission';
import { credentialWarnings } from '../../../lib/edi/transport';
import { fmt$2 } from './format';

// Where a run has got to on its way to GAMMIS, and what to do next.
export function SubmissionPanel({ subs = [], transportConfigured, transportBusy, onDownload, onSend, onMarkSent, onAcknowledgement }) {
  if (!subs.length) return null;
  const colour = (st) => st === 'accepted' ? 'var(--success)'
    : st === 'rejected' ? 'var(--danger)'
    : st === 'partially_accepted' ? 'var(--warn)'
    : st === 'sent' ? 'var(--info)' : 'var(--text3)';

  return (
    <div style={{ margin: '12px 0', border: '0.5px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ padding: '8px 12px', background: 'var(--bg2)', fontSize: 11, fontWeight: 600,
                    textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text3)' }}>
        Submissions to GAMMIS
      </div>
      {subs.map((sub, i) => (
        <div key={sub.id} style={{ padding: '10px 12px', borderTop: i ? '0.5px solid var(--border)' : 'none' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div>
              <span style={{ fontWeight: 600, fontSize: 12.5, color: colour(sub.status) }}>
                {sub.status.replace(/_/g, ' ')}
              </span>
              <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 8, fontFamily: 'monospace' }}>
                {sub.file_name}
              </span>
              {sub.usage_indicator === 'T' && (
                <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--warn)', marginLeft: 8 }}>TEST FILE</span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }}
                onClick={() => onDownload(sub)}>↓ File</button>
              {sub.status === 'generated' && transportConfigured && (
                <button className="btn btn-primary btn-sm" style={{ fontSize: 11 }}
                  disabled={!!transportBusy}
                  onClick={() => onSend(sub)}>↑ Send to GAMMIS</button>
              )}
              {sub.status === 'generated' && (
                <button className="btn btn-outline btn-sm" style={{ fontSize: 11, borderColor: 'var(--accent)', color: 'var(--accent)' }}
                  onClick={() => onMarkSent(sub)}>Mark uploaded</button>
              )}
              {sub.status !== 'generated' && (
                <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }}
                  onClick={() => onAcknowledgement(sub)}>↑ Acknowledgement</button>
              )}
            </div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4, lineHeight: 1.5 }}>
            {[
              `${sub.claim_count} claim${sub.claim_count === 1 ? '' : 's'}`,
              sub.total_charge != null ? fmt$2(sub.total_charge) : null,
              `generated ${format(new Date(sub.generated_at), 'MMM d, HH:mm')}${sub.generated_by_name ? ` by ${sub.generated_by_name}` : ''}`,
              sub.sent_at ? `uploaded ${format(new Date(sub.sent_at), 'MMM d, HH:mm')}${sub.channel === 'sftp' ? ' over SFTP' : ''}` : null,
              sub.ack_824_received_at ? `824 ${sub.ack_824_status}` : null,
            ].filter(Boolean).join(' · ')}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4 }}>
            {nextStep(sub, { automatic: transportConfigured })}
          </div>
          {Array.isArray(sub.problems) && sub.problems.length > 0 && (
            <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 11, color: 'var(--danger)', lineHeight: 1.5 }}>
              {sub.problems.map((p, n) => <li key={n}>{p}</li>)}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

// The state of the wire: whether it is configured, what it last did, and
// how long the credentials have left. The last of those is not decoration —
// the SFTP password expires after a year and the warning is given at
// interactive sign-on, which an automated client never performs, so this is
// the only place anyone will ever see it coming.
export function TransportPanel({ settings, transportConfigured, transport, transfers, anySubmissions, onClaimStatus, onRefresh }) {
  const warnings = credentialWarnings({
    passwordSetOn: settings['SFTP Password Set On'] || null,
    pgpKeyCreatedOn: settings['PGP Key Created On'] || null,
  }).filter(w => transportConfigured || w.severity !== 'info');

  const busy = transport?.busy;
  const pushed = transport?.result?.push;
  const pulled = transport?.result?.pull;

  // Nothing to say while there is nothing to file: the identity banner
  // already reports that enrolment is incomplete, and a second box saying
  // the same thing every week is how banners stop being read. Once a file
  // has actually been generated, the absence of a Send button needs
  // explaining, so from then on this appears.
  if (!transportConfigured && !warnings.length && !anySubmissions) return null;

  const colour = { expired: 'var(--danger)', urgent: 'var(--danger)', warning: 'var(--warn)', info: 'var(--text3)' };

  return (
    <div style={{ margin: '0 14px 12px', border: '0.5px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ padding: '8px 12px', background: 'var(--bg2)', display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text3)' }}>
          GAMMIS transfer {transportConfigured ? '' : '— not configured'}
        </span>
        {transportConfigured && (
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }} disabled={!!busy}
              onClick={onClaimStatus} title="Send a 276 for every claim still waiting on an answer">
              ? Claim status
            </button>
            <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }} disabled={!!busy}
              onClick={onRefresh}>
              {busy ? '…' : '↻ Check GAMMIS'}
            </button>
          </div>
        )}
      </div>

      <div style={{ padding: '10px 12px', fontSize: 11.5, color: 'var(--text2)', lineHeight: 1.6 }}>
        {busy && <div style={{ color: 'var(--info)' }}>{busy}…</div>}
        {transport?.error && <div style={{ color: 'var(--danger)' }}>{transport.error}</div>}

        {pushed && (
          <div>
            {pushed.sent.length
              ? `Sent ${pushed.sent.map(f => f.file).join(', ')}.`
              : 'Nothing was waiting to be sent.'}
            {pushed.failed.map((f, i) => (
              <div key={i} style={{ color: 'var(--danger)' }}>{f.error}</div>
            ))}
          </div>
        )}

        {transport?.result?.status && (
          <div>
            {transport.result.status.asked
              ? `Asked GAMMIS about ${transport.result.status.asked} claim${transport.result.status.asked === 1 ? '' : 's'}. The answer arrives as a 277 and is applied automatically.`
              : transport.result.status.note}
            {(transport.result.status.skipped || []).map((sk, i) => (
              <div key={i} style={{ color: 'var(--warn)' }}>{sk.claim} — {sk.why}</div>
            ))}
          </div>
        )}

        {pulled && (
          <div>
            {pulled.got.length
              ? pulled.got.map((g, i) => <div key={i}>{g.kind}: {g.note}</div>)
              : <div>Nothing new from GAMMIS.</div>}
            {pulled.failed.map((f, i) => (
              <div key={i} style={{ color: 'var(--danger)' }}>{f.file} — {f.error}</div>
            ))}
            {pulled.remaining > 0 && <div>{pulled.remaining} more waiting; press again.</div>}
          </div>
        )}

        {!busy && !transport && transportConfigured && transfers.length > 0 && (
          <div style={{ color: 'var(--text3)' }}>
            Last transfer {format(new Date(transfers[0].started_at), 'MMM d, HH:mm')} —{' '}
            {transfers[0].direction} {transfers[0].kind || 'file'}, {transfers[0].status}.
          </div>
        )}

        {!transportConfigured && (
          <div>
            Files are uploaded by hand until the SFTP account is live. The two directory
            names arrive in the Gainwell welcome letter and go on Settings → General Info.
          </div>
        )}

        {warnings.map((w, i) => (
          <div key={i} style={{ color: colour[w.severity], marginTop: 4 }}>{w.message}</div>
        ))}
      </div>
    </div>
  );
}

// What pre-flight said about the last export attempt, rendered under the
// run list. Blocking problems are the reason nothing was produced; warnings
// accompany a file that was.
export function ExportProblems({ problems, onDismiss }) {
  if (!problems) return null;
  const { blocking = [], warnings = [], exported, heading } = problems;
  if (!blocking.length && !warnings.length) return null;
  return (
    <div style={{
      margin: '0 14px 12px', padding: '12px 14px', borderRadius: 8,
      border: '0.5px solid ' + (blocking.length ? 'var(--danger)' : 'var(--warn)'),
      background: blocking.length ? 'var(--danger-bg)' : 'var(--warn-bg)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: blocking.length ? 'var(--danger)' : 'var(--warn)' }}>
          {heading || (blocking.length
            ? 'This run cannot be filed yet'
            : exported ? 'File exported, with warnings' : 'Warnings')}
        </div>
        <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }}
          onClick={onDismiss}>Dismiss</button>
      </div>
      {blocking.length > 0 && (
        <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12.5, lineHeight: 1.6, color: 'var(--text)' }}>
          {blocking.map((p, i) => <li key={i}>{p}</li>)}
        </ul>
      )}
      {warnings.length > 0 && (
        <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12.5, lineHeight: 1.6, color: 'var(--text2)' }}>
          {warnings.map((p, i) => <li key={i}>{p}</li>)}
        </ul>
      )}
    </div>
  );
}
